/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useIntl, FormattedMessage} from 'react-intl'

import {
    Flex,
    Button,
    Skeleton,
    Box,
    Text,
    VStack,
    Fade,
    useMultiStyleConfig,
    Divider,
} from '@chakra-ui/react'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import Link from '@salesforce/retail-react-app/app/components/link'

// project components
import Benefits from '../benefits'
import Breadcrumb from '../breadcrumb'
import ImageGallery, {Skeleton as ImageGallerySkeleton} from '../image-gallery'
import {InformativeLabelGroup} from '../informative-label'

import ProductHeader from './product-header'
import ProductVariants from './product-variants'
import KlarnaWidget from '../klarna-widget'
import EnergyLabel from '../../components/energy-label'
import {ADD_TO_CART_ERROR_MESSAGES} from '../../constants'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useToast} from '../../hooks/use-toast'
import WhereToBuyModal from '../modals/whereToBuyModal'
import StockLabel from '../stock-label'
import CloseIcon from '../icons/action/CloseIcon'
import {InfoIcon} from '../icons/others'
import {getPriceData} from '../../utils/product-utils'
import PriceCard from '../price-card'
import {remCalc} from '../../theme/_utils'
import {useDerivedProduct} from '../../hooks/use-derived-product'
import {useCustomPreference, useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import NotifyMeButton from '../notify-me-button'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'

const ProductViewHeader = ({category, isProductLoading}) => {
    const style = useMultiStyleConfig('ProductView')
    return isProductLoading ? (
        <Skeleton my={remCalc(16)} h={remCalc(24)} w={{base: '100%', md: remCalc(350)}} />
    ) : (
        <Box {...style.breadcrumbContainer}>
            <Breadcrumb categories={category} />
        </Box>
    )
}

ProductViewHeader.propTypes = {
    name: PropTypes.string,
    shortDescription: PropTypes.string,
    currency: PropTypes.string,
    category: PropTypes.array,
    priceData: PropTypes.object,
    product: PropTypes.object,
    isProductPartOfBundle: PropTypes.bool,
    addToWishlist: PropTypes.func,
    updateWishlist: PropTypes.func,
    isWishlistLoading: PropTypes.bool,
    isProductPartOfSet: PropTypes.bool,
    isProductLoading: PropTypes.bool,
}

//Context to avoid prop drilling the images alt text
export const ProductImageAltTextContext = React.createContext()

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */

const ProductView = forwardRef(
    (
        {
            product,
            category,
            imageSize = 'md',
            isWishlistLoading = false,
            addToCart,
            updateCart,
            addToWishlist,
            updateWishlist,
            isProductLoading,
            isProductPartOfSet = false,
            isProductPartOfBundle = false,
            childOfBundleQuantity = 0,
            childProductOrderability,
            setChildProductOrderability,
            isBasketLoading = false,
            onVariantSelected = () => {},
            validateOrderability = (variant, quantity, stockLevel) =>
                !isProductLoading && quantity > 0 && quantity <= stockLevel,
            showImageGallery = true,
            // setSelectedBundleQuantity = () => {},
            selectedBundleParentQuantity = 1,
        },
        ref,
    ) => {
        const imageGalleryStyles = useMultiStyleConfig('ImageGallery')
        const {currency: activeCurrency} = useCurrency()
        const showToast = useToast()
        const intl = useIntl()
        const location = useLocation()
        const {
            isOpen: isAddToCartModalOpen,
            onOpen: onAddToCartModalOpen,
            onClose: onAddToCartModalClose,
        } = useAddToCartModalContext()
        const [showOptionsMessage, toggleShowOptionsMessage] = useState(false)
        const [isLoadingAddToCart, setIsLoadingAddToCart] = useState(false)
        const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
        const {data: basket} = useCurrentBasket()
        const {addLayer} = useGoogleTagManager()
        const {currency} = useCurrency()

        const {
            showLoading,
            showInventoryMessage,
            inventoryMessage,
            quantity,
            // minOrderQuantity,
            setQuantity,
            variant,
            variationParams,
            variationAttributes,
            stockLevel,
            // stepQuantity,
            isOutOfStock,
            unfulfillable,
        } = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle)

        const priceData = useMemo(() => {
            return getPriceData(product, {quantity})
        }, [product, quantity])

        const isProductASet = product?.type?.set
        const isProductABundle = product?.type?.bundle
        const errorContainerRef = useRef(null)
        const style = useMultiStyleConfig('ProductView')
        const handleWishlistItem = async () => {
            if (!updateWishlist && !addToWishlist) return null
            if (updateWishlist) {
                updateWishlist(product, variant, quantity)
                return
            }
            addToWishlist(product, variant, quantity)
        }
        const [isModalOpen, setIsModalOpen] = useState(false)

        const {customInventoryMessage} = useMemo(() => {
            let shouldDisableButton = showInventoryMessage
            let currentInventoryMsg = ''
            if (
                !shouldDisableButton &&
                (isProductASet || isProductABundle) &&
                childProductOrderability
            ) {
                // if any of the children are not orderable, it will disable the add to cart button
                const unavailableChildProductKey = Object.keys(childProductOrderability).find(
                    (key) => {
                        return childProductOrderability[key].showInventoryMessage
                    },
                )
                shouldDisableButton = !!unavailableChildProductKey
                if (unavailableChildProductKey) {
                    const unavailableChildProduct =
                        childProductOrderability[unavailableChildProductKey]
                    if (unavailableChildProduct.unfulfillable) {
                        currentInventoryMsg = intl.formatMessage(
                            ADD_TO_CART_ERROR_MESSAGES.INVENTORY_REMAINING_FOR_PRODUCT,
                            {
                                stockLevel: unavailableChildProduct.stockLevel,
                                productName: unavailableChildProduct.productName,
                            },
                        )
                    }
                    if (unavailableChildProduct.isOutOfStock) {
                        currentInventoryMsg = intl.formatMessage(
                            ADD_TO_CART_ERROR_MESSAGES.OUT_OF_STOCK_FOR_PRODUCT,
                            {productName: unavailableChildProduct.productName},
                        )
                    }
                }
            }
            return {disableButton: shouldDisableButton, customInventoryMessage: currentInventoryMsg}
        }, [showInventoryMessage, childProductOrderability])

        const buttonDisabled = !product?.inventory?.orderable && !product?.c_isPDPWhereToBuy

        const validateAndShowError = (opts = {}) => {
            const {scrollErrorIntoView = true} = opts
            // Validate that all attributes are selected before proceeding.
            const hasValidSelection = validateOrderability(variant, quantity, stockLevel)
            const showError = !isProductASet && !isProductABundle && !hasValidSelection
            const scrollToError = showError && scrollErrorIntoView

            toggleShowOptionsMessage(showError)

            if (scrollToError) {
                errorContainerRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                })
            }

            return hasValidSelection
        }

        const renderActionButtons = () => {
            const buttons = []
            const buttonText = {
                update: intl.formatMessage({
                    defaultMessage: 'Update',
                    id: 'product_view.button.update',
                }),
                addToCart: intl.formatMessage({
                    defaultMessage: 'Add to Cart',
                    id: 'global.add-to-cart',
                }),
                addSetToCart: intl.formatMessage({
                    defaultMessage: 'Add Set to Cart',
                    id: 'product_view.button.add_set_to_cart',
                }),
                addBundleToCart: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Cart',
                    id: 'product_view.button.add_bundle_to_cart',
                }),
                whereToBuy: intl.formatMessage({
                    defaultMessage: 'Where to buy',
                    id: 'product_view.button.where_to_buy',
                }),
            }

            const showError = () => {
                showToast({
                    title: intl.formatMessage(API_ERROR_MESSAGE),
                    status: 'error',
                })
            }
            const addToCartEventPDP = async () => {
                await addLayer(polish_ecommerce_gtm_event.bind(this))
                await addLayer(
                    item_gtm_event.bind(this, {
                        event: 'add_to_cart',
                        currency: currency,
                        bundle: 'false',
                        items: [
                            {
                                item_name: product?.c_defaultName,
                                item_id: product?.id,
                                item_category: product?.c_defaultCategoryName,
                                item_variant: product?.c_subModelIdentifier,
                                price: parseFloat(
                                    (
                                        product?.price /
                                        (1 + parseFloat(product?.c_taxRate) / 100)
                                    ).toFixed(2),
                                ),
                                fullprice: getPriceData(product)?.isOnSale
                                    ? 'reduced'
                                    : 'fullprice',
                                rating_points: '',
                                // get_reevoo_rating() || get_bv_rating(),
                                erp_category: product?.c_materialGroup,
                                prdha_category: product?.c_prdha_category,
                                quantity: quantity,
                            },
                        ],
                    }),
                )
            }
            const handleCartItem = async () => {
                // check if there is already a customized product in the cart
                if (basket?.productItems?.[0]?.c_officinaConfigurationId) {
                    return showToast({
                        title: intl.formatMessage({
                            defaultMessage:
                                'Since you are adding a customized product to your cart, your order cannot include other types of products. Before adding other products to your cart, complete your order.',
                            id: 'officina.error.complete_your_order',
                        }),
                        status: 'error',
                    })
                }

                setIsLoadingAddToCart(true)
                const hasValidSelection = validateAndShowError()

                if (!hasValidSelection) {
                    return null
                }

                if (!addToCart && !updateCart) return null
                if (updateCart) {
                    await updateCart(variant || product, quantity)
                    return
                }
                try {
                    const itemsAdded = await addToCart(variant, quantity)
                    // Open modal only when `addToCart` returns some data
                    // It's possible that the item has been added to cart, but we don't want to open the modal.
                    // See wishlist_primary_action for example.
                    if (itemsAdded) {
                        onAddToCartModalOpen({
                            product,
                            itemsAdded,
                            selectedQuantity: quantity,
                        })
                    }
                } catch (e) {
                    showError()
                }
                setIsLoadingAddToCart(false)
                addToCartEventPDP()
            }

            const handleButtonClick = () => {
                if (product?.inventory?.orderable === false && product?.c_isPDPWhereToBuy) {
                    setIsModalOpen(true)
                } else {
                    handleCartItem()
                }
            }

            const handleCloseModal = () => {
                setIsModalOpen(false)
            }

            // child product of bundles do not have add to cart button
            if (
                (addToCart || updateCart) &&
                !isProductPartOfBundle &&
                !buttonDisabled &&
                !notShoppableCountry
            ) {
                buttons.push(
                    <Button
                        className="js-cart-add"
                        data-product-code={product.id}
                        key="cart-button"
                        onClick={handleButtonClick}
                        isDisabled={buttonDisabled}
                        isLoading={isBasketLoading || isLoadingAddToCart}
                        width="100%"
                        variant="primary"
                    >
                        {product?.inventory?.orderable === false && product?.c_isPDPWhereToBuy
                            ? buttonText.whereToBuy
                            : updateCart
                            ? buttonText.update
                            : isProductASet
                            ? buttonText.addSetToCart
                            : isProductABundle
                            ? buttonText.addBundleToCart
                            : buttonText.addToCart}
                    </Button>,
                    <WhereToBuyModal
                        key="where-to-buy-modal"
                        isOpen={isModalOpen}
                        onClose={handleCloseModal}
                    />,
                )
            }

            return buttons
        }

        // Bind the reference with our `scope` that includes the internal validate function for this component.
        // Other values can be added to this scope as required.
        if (typeof ref === 'function') {
            ref = ref.bind({
                validateOrderability: validateAndShowError,
                renderActionButtons: renderActionButtons,
            })
        }

        // Set the quantity of bundle child in a product bundle to ensure availability messages appear
        if (
            isProductPartOfBundle &&
            quantity != selectedBundleParentQuantity * childOfBundleQuantity
        ) {
            setQuantity(selectedBundleParentQuantity * childOfBundleQuantity)
        }

        useEffect(() => {
            if (isAddToCartModalOpen) {
                onAddToCartModalClose()
            }
        }, [location.pathname])

        useEffect(() => {
            if (
                !isProductASet &&
                !isProductABundle &&
                validateOrderability(variant, quantity, stockLevel)
            ) {
                toggleShowOptionsMessage(false)
            }
        }, [variationParams])

        useEffect(() => {
            if (variant) {
                onVariantSelected(product, variant, quantity)
            }
        }, [variant?.productId, quantity])

        useEffect(() => {
            if (isProductPartOfBundle || isProductPartOfSet) {
                const key = product.itemId ?? product.id
                // when showInventoryMessage is true, it means child product is not orderable
                setChildProductOrderability((previousState) => ({
                    ...previousState,
                    [key]: {
                        showInventoryMessage,
                        isOutOfStock,
                        unfulfillable,
                        stockLevel,
                        productName: product?.name,
                    },
                }))
            }
        }, [showInventoryMessage, inventoryMessage])

        const lastItemThreshold = useCustomPreference('showLastItemThreshold')
        const digiohVendorID = useCustomPreference('digiohVendorID')
        const subscriptionRedirectPDP = useCustomPreference('subscriptionRedirectPDP')

        const subscribeButton = product?.c_recommendations?.find(
            (rec) => rec?.recommendationType?.value === 4,
        ) &&
            subscriptionRedirectPDP && (
                <Button
                    w="100%"
                    mt={remCalc(8)}
                    variant="secondary"
                    as={Link}
                    to={subscriptionRedirectPDP}
                    // onClick={() =>
                    //     onAddToCartModalOpen({
                    //         itemsAdded: [{product, quantity: 1}],
                    //         isSubscription: true,
                    //     })
                    // }
                >
                    <FormattedMessage
                        defaultMessage="Buy in subscription"
                        id="product_item.subscription.button.buy_subscription"
                    />
                </Button>
            )

        const productImageAltText = `${product?.c_categoryName}, ${product?.name}, ${product?.variationValues?.skuIdentifier}, ${product?.c_marketingColour}`

        return (
            <Flex {...style.flexContainer} data-testid="product-view" ref={ref}>
                <ProductViewHeader category={category} isProductLoading={isProductLoading} />
                <Flex {...style.productContainer}>
                    {showImageGallery && (
                        <Box {...style.imageGalleryBox}>
                            {product && !isProductLoading ? (
                                <>
                                    <Flex {...imageGalleryStyles.infoLabelContainer}>
                                        <InformativeLabelGroup
                                            product={product}
                                            priceData={priceData}
                                        />
                                    </Flex>
                                    <ProductImageAltTextContext.Provider
                                        value={productImageAltText}
                                    >
                                        <ImageGallery
                                            key={product.id}
                                            size={imageSize}
                                            imageGroups={product.imageGroups}
                                            powerImages={product?.c_localizedPowerImages}
                                            selectedVariationAttributes={variationParams}
                                            lazy={isProductPartOfSet || isProductPartOfBundle}
                                            videoUrl={product?.c_youtubeVideoGeneric}
                                        />
                                    </ProductImageAltTextContext.Provider>
                                </>
                            ) : (
                                <ImageGallerySkeleton />
                            )}
                        </Box>
                    )}
                    <Divider {...style.horizontalDivider} />
                    {/* Variations & Quantity Selector & CTA buttons */}
                    <VStack {...style.productCard}>
                        <Box {...style.productHeaderContainer}>
                            {isProductLoading ? (
                                <VStack gap={remCalc(8)}>
                                    <Skeleton h={remCalc(32)} w="100%" />
                                    <Skeleton h={remCalc(12)} w="100%" />
                                    <Skeleton h={remCalc(12)} w="100%" />
                                    <Skeleton h={remCalc(12)} w="100%" />
                                </VStack>
                            ) : (
                                <ProductHeader
                                    name={product?.name}
                                    product={product}
                                    handleWishlistItem={handleWishlistItem}
                                    isWishlistLoading={isWishlistLoading}
                                />
                            )}
                        </Box>
                        {variationAttributes.length && (
                            <VStack align="stretch" gap={0} w="full">
                                {isProductPartOfBundle && (
                                    <Box>
                                        <Text fontWeight="medium" fontSize="md" aria-label="price">
                                            <label>
                                                {intl.formatMessage({
                                                    defaultMessage: 'Quantity',
                                                    id: 'product_view.label.quantity',
                                                })}
                                                : {childOfBundleQuantity}
                                            </label>
                                        </Text>
                                    </Box>
                                )}
                                {/*
                                Customize the skeletons shown for attributes to suit your needs. At the point
                                that we show the skeleton we do not know how many variations are selectable. So choose
                                a a skeleton that will meet most of your needs.
                            */}
                                {isProductLoading ? (
                                    <>
                                        <Skeleton height={20} width={64} />
                                    </>
                                ) : (
                                    <ProductVariants
                                        variationAttributes={variationAttributes}
                                        product={product}
                                    />
                                )}
                                <Box ref={errorContainerRef}>
                                    {!showLoading && showOptionsMessage && (
                                        <Fade in={true}>
                                            <Text
                                                color="text.danger"
                                                fontWeight={600}
                                                marginBottom={8}
                                            >
                                                {intl.formatMessage(
                                                    ADD_TO_CART_ERROR_MESSAGES.NO_VARIANT_SELECTED,
                                                )}
                                            </Text>
                                        </Fade>
                                    )}
                                </Box>
                                {isProductASet && <p>{product?.shortDescription}</p>}
                                {!showLoading &&
                                    product?.inventory?.orderable &&
                                    product.inventory.stockLevel <= lastItemThreshold && (
                                        <Box {...style.inventoryLabelContainer}>
                                            <Fade in>
                                                <StockLabel
                                                    variant="lastFewItems"
                                                    icon={<InfoIcon circleColor="transparent" />}
                                                    textMessage={
                                                        <FormattedMessage
                                                            id="use_product.message.low_stock"
                                                            defaultMessage="Last {quantity} {itemLabel}"
                                                            values={{
                                                                quantity:
                                                                    product?.inventory?.stockLevel,
                                                                itemLabel:
                                                                    product?.inventory
                                                                        ?.stockLevel === 1 ? (
                                                                        <FormattedMessage
                                                                            id="global.message.item"
                                                                            defaultMessage="item"
                                                                        />
                                                                    ) : (
                                                                        <FormattedMessage
                                                                            id="global.message.items"
                                                                            defaultMessage="items"
                                                                        />
                                                                    ),
                                                            }}
                                                        />
                                                    }
                                                />
                                            </Fade>
                                        </Box>
                                    )}
                                {!showLoading &&
                                    !product?.inventory?.orderable &&
                                    !product?.c_isPDPWhereToBuy && (
                                        <Box {...style.inventoryLabelContainer}>
                                            <Fade in>
                                                <StockLabel
                                                    variant="outOfStock"
                                                    icon={<CloseIcon />}
                                                    textMessage={
                                                        <FormattedMessage
                                                            id="use_product.message.out_of_stock"
                                                            defaultMessage="Out of stock"
                                                        />
                                                    }
                                                />
                                            </Fade>
                                        </Box>
                                    )}
                            </VStack>
                        )}
                        <Divider {...style.horizontalDivider} />

                        <Box {...style.priceBox}>
                            {isProductLoading ? (
                                <Skeleton h={remCalc(76)} mb={remCalc(16)} w="100%" />
                            ) : (
                                <PriceCard
                                    priceData={priceData}
                                    product={product}
                                    activeCurrency={activeCurrency}
                                />
                            )}
                            <Box {...style.energyLabel}>
                                {product?.c_energyEfficiencyClass && (
                                    <EnergyLabel
                                        level={product.c_energyEfficiencyClass}
                                        description={intl.formatMessage({
                                            defaultMessage: 'Product information',
                                            id: 'energy_label.product_information',
                                        })}
                                        manualDocument={product?.c_manualDocument}
                                        textWidth={'big'}
                                        labelPosition="top"
                                    />
                                )}
                            </Box>
                            {!showLoading && customInventoryMessage && (
                                <Fade in={true}>
                                    <Text color="orange.600" fontWeight={600} marginBottom={8}>
                                        {customInventoryMessage}
                                    </Text>
                                </Fade>
                            )}
                            {product?.c_isPDPOnly !== 'true' && (
                                <>
                                    {product?.inventory?.stockLevel === 0 && (
                                        <NotifyMeButton product={product} />
                                    )}
                                    <Box
                                        display={
                                            buttonDisabled
                                                ? 'none'
                                                : isProductPartOfSet
                                                ? 'block'
                                                : ['none', 'none', 'none', 'block']
                                        }
                                    >
                                        {renderActionButtons()}
                                        {subscribeButton}
                                    </Box>
                                    <Box
                                        display={
                                            buttonDisabled
                                                ? 'none'
                                                : isProductPartOfSet || isProductPartOfBundle
                                                ? 'none'
                                                : ['block', 'block', 'block', 'none']
                                        }
                                    >
                                        {renderActionButtons()}
                                        {subscribeButton}
                                    </Box>
                                </>
                            )}

                            <Box {...style.klarnaBox}>
                                {priceData?.currentPrice ? (
                                    <KlarnaWidget price={priceData}></KlarnaWidget>
                                ) : (
                                    <></>
                                )}
                                {digiohVendorID && <div id="digioh-pdp-container"></div>}
                                <Benefits />
                            </Box>
                        </Box>
                    </VStack>
                </Flex>
            </Flex>
        )
    },
)

ProductView.displayName = 'ProductView'

ProductView.propTypes = {
    product: PropTypes.object,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    childOfBundleQuantity: PropTypes.number,
    category: PropTypes.array,
    isProductLoading: PropTypes.bool,
    isBasketLoading: PropTypes.bool,
    isWishlistLoading: PropTypes.bool,
    addToCart: PropTypes.func,
    addToWishlist: PropTypes.func,
    updateCart: PropTypes.func,
    updateWishlist: PropTypes.func,
    showFullLink: PropTypes.bool,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    childProductOrderability: PropTypes.object,
    setChildProductOrderability: PropTypes.func,
    onVariantSelected: PropTypes.func,
    validateOrderability: PropTypes.func,
    showImageGallery: PropTypes.bool,
    setSelectedBundleQuantity: PropTypes.func,
    selectedBundleParentQuantity: PropTypes.number,
}

export default ProductView
